import React, { useState, useRef, useEffect } from "react";
import Tesseract from "tesseract.js";
import ImageCropper from "../image-cropper/cropper";

export default function ReceiptScanner({ onTextExtracted }) {
  const [originalImage, setOriginalImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState("");
  const [showCropper, setShowCropper] = useState(false);
  const [wordBoxes, setWordBoxes] = useState([]);
  const imageRef = useRef(null);
  const canvasRef = useRef(null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setOriginalImage(imageUrl);
      setShowCropper(true);
      setWordBoxes([]);
      setResult("");
    }
  };

  const handleCropComplete = (croppedImgUrl) => {
    setCroppedImage(croppedImgUrl);
    setShowCropper(false);
  };

  const preprocessImage = (imageSrc) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = imageSrc;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");

        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Convert to grayscale + normalize contrast + invert
        for (let i = 0; i < data.length; i += 4) {
          const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
          // Threshold and normalize contrast
          const value = avg < 170 ? 0 : 255;
          // Invert to white background and black text
          const inverted = 255 - value;
          data[i] = data[i + 1] = data[i + 2] = inverted;
        }

        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL());
      };
    });
  };

  const handleScan = async () => {
    if (!croppedImage) return;

    setScanning(true);

    const preprocessed = await preprocessImage(croppedImage);

    Tesseract.recognize(preprocessed, "eng", {
      tessedit_char_whitelist:
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789.:/- RM",
    })
      .then(({ data }) => {
        setResult(data.text);
        setWordBoxes(data.words || []);
        onTextExtracted && onTextExtracted(data.text);
      })
      .catch((err) => console.error("OCR Error:", err))
      .finally(() => setScanning(false));
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image || !wordBoxes || wordBoxes.length === 0) return;

    const ctx = canvas.getContext("2d");
    canvas.width = image.width;
    canvas.height = image.height;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const scaleX = image.width / image.naturalWidth;
    const scaleY = image.height / image.naturalHeight;

    ctx.strokeStyle = "red";
    ctx.lineWidth = 2;

    wordBoxes.forEach((word) => {
      if (!word.bbox) return;
      const { x0, y0, x1, y1 } = word.bbox;
      ctx.strokeRect(
        x0 * scaleX,
        y0 * scaleY,
        (x1 - x0) * scaleX,
        (y1 - y0) * scaleY
      );
    });
  }, [wordBoxes, croppedImage]);

  return (
    <div>
      <label htmlFor="upload-input" style={uploadBtn}>
        📷 Upload Receipt
      </label>
      <input
        id="upload-input"
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        style={{ display: "none" }}
      />
      <br />

      {showCropper && originalImage && (
        <ImageCropper
          imageSrc={originalImage}
          onCropComplete={handleCropComplete}
          onCancel={() => setShowCropper(false)}
        />
      )}

      {!showCropper && croppedImage && (
        <div
          style={{
            position: "relative",
            display: "inline-block",
            marginTop: "1rem",
          }}
        >
          <img
            ref={imageRef}
            src={croppedImage}
            alt="Cropped"
            style={{ maxWidth: "100%", display: "block" }}
            onLoad={() => {
              if (canvasRef.current) {
                canvasRef.current.width = imageRef.current.width;
                canvasRef.current.height = imageRef.current.height;
              }
            }}
          />
          <canvas
            ref={canvasRef}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              pointerEvents: "none",
            }}
          />
          <br />
          <button onClick={handleScan} disabled={scanning} style={scanBtn}>
            {scanning ? "Scanning..." : "Scan Receipt"}
          </button>
        </div>
      )}

      {result && (
        <div style={{ whiteSpace: "pre-wrap", marginTop: "1rem" }}>
          <strong>Scanned Text:</strong>
          <br />
          {result}
        </div>
      )}
    </div>
  );
}

const scanBtn = {
  padding: "0.5rem 1rem",
  backgroundColor: "#4a90e2",
  color: "white",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
};

const uploadBtn = {
  display: "inline-block",
  padding: "0.5rem 1rem",
  backgroundColor: "#4a90e2",
  color: "white",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  textAlign: "center",
};
